import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { debounce, isEqual } from "lodash";

import { Dropdown } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { IconDropdownButton } from "app/pages/sales-intelligence/modules/enrichment/components/IconDropdownButton/IconDropdownButton";
import type { FieldItem } from "app/pages/sales-intelligence/modules/enrichment/store/type";
import type {
    ConfigField,
    ConfigType,
    CsvExportColumnCategory,
} from "app/pages/sales-intelligence/modules/enrichment/types/columns";
import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import { APPLY, CONFIG_SEARCH_TEXT } from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";

import {
    StyledAdditionalLabel,
    StyledDropdown,
    StyledEllipsisDropdownItem,
    StyledExample,
    StyledHighlight,
    StyledTech,
    StyledTextWrapper,
} from "./style";

type FieldsDropdownProps = {
    configName: CsvExportColumnCategory;
    configLabel: string;
    selectedFields: readonly ConfigField[];
    fields: readonly FieldItem[];
    type: EnrichmentType;
    renderFieldBadge?: (field: ConfigField) => ReactNode;
    onApply: (configName: string, selectedOptions: readonly ConfigField[]) => void;
};

export const FieldsDropdown = ({
    configName,
    fields,
    configLabel,
    selectedFields,
    type,
    renderFieldBadge,
    onApply,
}: FieldsDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasFooter, setHasFooter] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [internalOptions, setInternalOptions] = useState<readonly ConfigField[]>([]);

    useEffect(() => {
        if (selectedFields) {
            setInternalOptions(selectedFields);
        }
    }, [selectedFields]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<Dropdown<unknown>>(null);

    useEffect(() => {
        setHasFooter(!isEqual(selectedFields, internalOptions));
    }, [selectedFields, internalOptions]);

    const handleApply = () => {
        setIsOpen(false);
        // @ts-expect-error outdated and common approach in the codebase
        dropdownRef.current?.closePopup();
        onApply(configName, internalOptions);

        enrichmentTrackingService.csv.trackApplyFields(configLabel, internalOptions.length, type);
    };

    const isDisabled = selectedFields.length === internalOptions.length;

    const footer = () => {
        return hasFooter ? (
            <FooterButton
                disabled={isDisabled}
                onClick={handleApply}
            >
                {translate(APPLY)}
            </FooterButton>
        ) : null;
    };

    const onToggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen && scrollRef.current) {
            scrollRef.current.scrollIntoView({
                block: "start",
                inline: "nearest",
            });
            return;
        }

        enrichmentTrackingService.csv.trackOpenCategory(configLabel, selectedFields.length, type);
    };

    const onClick = ({ id, type: configType, alias }: { id: string; type: ConfigType; alias: string }) => {
        const selectedItem = internalOptions.find((item) => item.name === id);

        if (!selectedItem) {
            enrichmentTrackingService.csv.trackSelectField(id, configLabel, internalOptions.length, type);
            return setInternalOptions(internalOptions.concat({ name: id, alias, type: configType }));
        }

        enrichmentTrackingService.csv.trackDeselectField(id, configLabel, internalOptions.length, type);
        setInternalOptions(internalOptions.filter((item) => item.name !== id));
    };

    const allOptionsNumber = fields.length;
    const numberOfSelectedOptions = selectedFields.length;

    const handleClose = () => {
        if (isOpen) {
            setInternalOptions(selectedFields);
        }
    };

    const highlightText = (text?: string, highlight?: string) => {
        if (!text || !highlight) {
            return <span>{text}</span>;
        }

        const parts = text.split(new RegExp(`(${highlight})`, "gi"));

        return parts.map((part, i) => {
            return part.toLowerCase() === highlight.toLowerCase() ? (
                <StyledHighlight key={i}>{part}</StyledHighlight>
            ) : (
                <span key={i}>{part}</span>
            );
        });
    };

    const items = [
        <IconDropdownButton
            isInfoVisible={!(selectedFields.length > 0)}
            isOpen={isOpen}
            title={configLabel}
            selectedNumber={numberOfSelectedOptions}
            allOptionsNumber={allOptionsNumber}
            data-automation={`enrichment-config-btn-${configName}`}
            key={`config-btn-${configName}`}
            onCloseClick={handleClose}
        />,

        ...fields
            .slice()
            .sort((a, b) => {
                const isCurrentSelected = selectedFields.some((x) => x.name === a.field.name);
                if (isCurrentSelected && selectedFields.some((x) => x.name === b.field.name)) {
                    return 0;
                }

                return isCurrentSelected ? -1 : 1;
            })
            .map(({ field, label, id, additionalLabel }, index) => {
                const text = highlightText(label, searchTerm);
                const subText = highlightText(additionalLabel, searchTerm);

                const subLabelWrapped =
                    configName === "technologies" ? (
                        <StyledTech>{subText}</StyledTech>
                    ) : (
                        <StyledExample>{subText}</StyledExample>
                    );

                const fieldBadge = renderFieldBadge?.(field);

                return (
                    <StyledEllipsisDropdownItem
                        showCheckBox
                        id={field.name}
                        alias={field.alias}
                        type={field.type}
                        text={label}
                        subText={additionalLabel}
                        key={id || index}
                    >
                        <Flex justifyContent="space-between">
                            <StyledTextWrapper>
                                {text}

                                {additionalLabel ? (
                                    <StyledAdditionalLabel>{subLabelWrapped}</StyledAdditionalLabel>
                                ) : null}
                            </StyledTextWrapper>

                            {fieldBadge ? <Flex margin="0 12px 0 0">{fieldBadge}</Flex> : null}
                        </Flex>
                    </StyledEllipsisDropdownItem>
                );
            }),
    ];

    const selectedIds = internalOptions.reduce(
        (acc, option) => {
            acc[option.name] = true;
            return acc;
        },
        {} as Record<string, boolean>,
    );

    const searchFilter = (term: string, { props: { subText, text } }: { props: { text: string; subText: string } }) => {
        const lowerCaseTerm = term.toLowerCase();
        const isFoundInLabel = text?.toLowerCase().includes(lowerCaseTerm);
        const isFoundInAdditionalLabel = subText?.toLowerCase().includes(lowerCaseTerm);

        return isFoundInLabel || isFoundInAdditionalLabel;
    };

    const onSearch = debounce((term: string) => {
        setSearchTerm(term);
        enrichmentTrackingService.csv.trackSearchInCategory(configLabel, term, type);
    }, 300);

    return (
        <StyledDropdown
            ref={scrollRef}
            className={`${configName}-container`}
        >
            <Dropdown
                ref={dropdownRef}
                onClick={onClick}
                selectedIds={selectedIds}
                footerComponent={footer}
                onToggle={onToggle}
                searchPlaceHolder={translate(CONFIG_SEARCH_TEXT, { configLabel })}
                hasSearch
                appendTo={`.${configName}-container`}
                // @ts-expect-error Unmatching type
                searchFilter={searchFilter}
                closeOnItemClick={false}
                virtualize={configName === "technologies"}
                debounce={50}
                onSearch={onSearch}
            >
                {items}
            </Dropdown>
        </StyledDropdown>
    );
};
